import React, { useMemo, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { NavigationContainer } from '@react-navigation/native'
import { createStackNavigator } from '@react-navigation/stack'
import { createDrawerNavigator } from '@react-navigation/drawer'
import { Portal } from 'react-native-paper'
import * as Linking from 'expo-linking'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { Bugsnag } from '../tools/general'

import CustomDrawer from './layouts/Main/CustomDrawer'

import { useTheme } from '../theming/ThemeProvider'
import { usePlatforms } from '../hooks/usePlatforms'

import Splash from '../pages/Splash'
import Login from '../pages/Login'
import ForgotPassword from '../pages/ForgotPassword'
import Dashboard from '../pages/Dashboard'
import Loads from '../pages/Loads'
import Tractors from '../pages/Tractors'
import Offers from '../pages/Offers'
import Orders from '../pages/Orders'
import LoadDetails from '../pages/LoadDetails'
import Profile from '../pages/Profile'
import PreferredLanes from '../pages/PreferredLanes'
import MyPayments from '../pages/MyPayments'
import { loginSelector } from '../redux/login/loginSelector'
import PaymentsConnect from '../pages/PaymentsConnect'
import { ErrorPage, LoadDetailsDrawer } from '../components'
import { Platform } from 'react-native'
import { getSecureStorage, storeGet } from '../tools/storage'
import FAQs from '../pages/FAQs'
import { getHotOffers, getOffers } from '../redux/initReducer/initActions'
import ENV from '../../env.json'
import APP from '../../app.json'
import { userSelector } from '../redux/user/userSelector'
import loginActions from '../redux/login/loginActions'

const prefix = Linking.makeUrl('/')

const PrivateStack = createStackNavigator()
const publicStack = createStackNavigator()
const MainDrawer = createDrawerNavigator()

const DrawerScreens = () => {
  const { isTablet } = usePlatforms()
  const { theme } = useTheme()
  return (
    <MainDrawer.Navigator
      initialRouteName='dashboard'
      sceneContainerStyle={{ backgroundColor: theme.WHITE }}
      drawerType={isTablet ? 'permanent' : 'front'}
      drawerContentOptions={{
        contentContainerStyle: { width: isTablet ? 100 : 280 }
      }}
      drawerStyle={{
        width: isTablet ? 100 : 280,
        borderRightWidth: isTablet ? 0 : 1
      }}
      drawerContent={(props) => (
        <CustomDrawer
          {...props} tableUp={isTablet}
        />
      )}
    >
      <MainDrawer.Screen name='dashboard' component={Dashboard} />
      <MainDrawer.Screen name='loads' component={Loads} />
      <MainDrawer.Screen name='tractors' component={Tractors} />
      <MainDrawer.Screen name='offers' component={Offers} />
      <MainDrawer.Screen name='orders' component={Orders} />
      <MainDrawer.Screen name='payments' component={MyPayments} />
      <MainDrawer.Screen name='payments-connect' component={PaymentsConnect} />
      {isTablet && Platform.OS === 'web' && <MainDrawer.Screen name='faqs' component={FAQs} />}
    </MainDrawer.Navigator>
  )
}

const PrivateScreens = () => (
  <PrivateStack.Navigator headerMode='none'>
    <PrivateStack.Screen name='main' component={DrawerScreens} />
    <PrivateStack.Screen name='load-details' component={LoadDetails} />
    {/* <PrivateStack.Screen name='change-email' component={ChangeEmail} /> */}
    <PrivateStack.Screen name='profile' component={Profile} />
    <PrivateStack.Screen name='preferred-lanes' component={PreferredLanes} />
  </PrivateStack.Navigator>
)

const PublicScreens = () => (
  <publicStack.Navigator headerMode='none'>
    <publicStack.Screen name='login' component={Login} />
    <publicStack.Screen name='forgot-password' component={ForgotPassword} />
  </publicStack.Navigator>
)

const AppNavigator = () => {
  const { rememberMe } = useSelector(userSelector)
  const { currentUser, didTryAutoLogin } = useSelector(loginSelector)
  const dispatch = useDispatch()
  const linking = {
    prefixes: [prefix]
  }

  Bugsnag.start({
    apiKey: 'a0c84a386f0622ee3603317efe8805a8',
    plugins: [new BugsnagPluginReact()],
    onError: async function (event) {
      const user = await storeGet()
      if (user) {
        event.setUser(user.user_id, user.email, user.name)
      }
    },
    appType: Platform.OS,
    releaseStage: ENV.stage === 'prod' ? 'production' : 'qa',
    appVersion: APP.expo.version
  })

  const ErrorBoundary = useMemo(() => Bugsnag.getPlugin('react').createErrorBoundary(React), [])

  const reLogin = useCallback(async () => {
    const user = await storeGet() || {}
    const { auth } = user
    if (rememberMe) {
      if (auth?.expiration - new Date().getTime() <= 0) {
        const credentials = await getSecureStorage('utils')
        dispatch(loginActions.login(credentials))
      }
    }
  }, [dispatch, rememberMe])

  useEffect(() => {
    if (currentUser) {
      reLogin()
    }
  }, [reLogin, currentUser])

  useEffect(() => {
    let interval
    if (currentUser) {
      interval = setInterval(async () => {
        await reLogin()
      }, 1800000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [currentUser, dispatch, reLogin])

  useEffect(() => {
    let interval
    if (currentUser) {
      interval = setInterval(() => {
        dispatch(getHotOffers())
        dispatch(getOffers())
      }, 900000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [dispatch, currentUser])

  if (!currentUser && !didTryAutoLogin) return <Splash />

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <Portal.Host>
        <NavigationContainer linking={linking}>
          {currentUser && <PrivateScreens />}
          {!currentUser && didTryAutoLogin && <PublicScreens />}
        </NavigationContainer>
      </Portal.Host>
      <LoadDetailsDrawer />
    </ErrorBoundary>
  )
}

export default AppNavigator
